import {
  IStripePaymentMethod,
  PaymentMethodsStore,
  UpdatePaymentMethodMutateFunction,
  StripeWithPaymentMethodType,
  CreateStripePaymentMethodMutateFunction,
  IUseCreateStripePaymentMethodProps,
} from "./types";
import { IDataFetcherResponseError } from "@common/utils/use-query/types";
import { DEFAULT_ERROR_MESSAGE } from "@common/utils/use-query/constants";
import { useMutation, useQuery, useQueryClient } from "@common/utils/use-query";
import { useState, useCallback } from "react";

export const paymentMethodsStore: PaymentMethodsStore = {
  useCreateStripeSetupIntent() {
    return useMutation({
      url: "/account/create_setup_intent",
      method: "POST",
    });
  },

  useCreateStripePaymentMethod(
    { invalidate }: IUseCreateStripePaymentMethodProps = { invalidate: true },
  ) {
    const {
      isPending: loadingStripeSetupIntent,
      error: stripeSetupIntentError,
      mutate: createStripeSetupIntent,
    } = this.useCreateStripeSetupIntent();

    // Properties
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<IDataFetcherResponseError | null>(null);

    // Query
    const queryClient = useQueryClient();
    const createStripePaymentMethod =
      useCallback<CreateStripePaymentMethodMutateFunction>(
        async (variables, options) => {
          const { card, stripe, billing_details } = variables;

          setError(null);
          setIsLoading(true);

          // 1. get client secret
          createStripeSetupIntent(null, {
            onError(error) {
              setError(error);
              setIsLoading(false);
            },
            onSuccess(
              stripeSetupIntent,
              updatedVariables,
              onMutateResult,
              context,
            ) {
              const { client_secret } = stripeSetupIntent || {};
              if (!client_secret) {
                setError({
                  status: 500,
                  message: "Missing Stripe client_secret",
                });
                setIsLoading(false);
              } else {
                const { name, email } = billing_details;

                // 2. confirm card
                stripe
                  .confirmCardSetup(client_secret, {
                    payment_method: {
                      card,
                      billing_details: { name, email },
                    },
                  })
                  .then(
                    ({ error: confirmStripeCardSetupError, setupIntent }) => {
                      // error
                      if (confirmStripeCardSetupError) {
                        const message =
                          confirmStripeCardSetupError.code !==
                          "incomplete_number"
                            ? confirmStripeCardSetupError.message ||
                              confirmStripeCardSetupError.code ||
                              confirmStripeCardSetupError.decline_code ||
                              DEFAULT_ERROR_MESSAGE
                            : null;
                        const status = message ? 500 : null;
                        const formattedError =
                          status && message
                            ? {
                                status,
                                message,
                              }
                            : null;

                        if (formattedError) {
                          setError(formattedError);
                          options?.onError?.(
                            formattedError,
                            variables,
                            onMutateResult,
                            context,
                          );
                        } else {
                          setError(null);
                        }

                        setIsLoading(false);
                      }
                      // success
                      else {
                        setError(null);
                        setIsLoading(false);

                        options?.onSuccess?.(
                          String(setupIntent.payment_method),
                          variables,
                          onMutateResult,
                          context,
                        );

                        if (invalidate) {
                          queryClient.invalidateQueries({
                            queryKey: ["/account/payment_methods"],
                          });
                        }
                      }
                    },
                  );
              }
            },
          });
        },
        [createStripeSetupIntent, invalidate, queryClient],
      );

    return {
      isPending: isLoading || loadingStripeSetupIntent,
      error: error || stripeSetupIntentError,
      mutate: createStripePaymentMethod,
    };
  },

  useUpdatePaymentMethod() {
    const {
      isPending: loadingCreateStripePaymentMethod,
      error: createStripePaymentMethodError,
      mutate: createStripePaymentMethod,
    } = this.useCreateStripePaymentMethod({ invalidate: false });

    // Update card
    const {
      isPending: loadingCreatePaymentMethod,
      error: createPaymentMethodError,
      mutate: createPaymentMethod,
    } = useMutation<unknown, unknown, StripeWithPaymentMethodType>({
      url: "/account/payment_methods/new",
      method: "POST",
    });

    // Properties
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<IDataFetcherResponseError | null>(null);

    // Query
    const queryClient = useQueryClient();
    const updatePaymentMethod = useCallback<UpdatePaymentMethodMutateFunction>(
      async (variables, options) => {
        const { card, stripe, billing_details } = variables;

        setError(null);
        setIsLoading(true);

        // 1. create Stripe payment method
        const { name, email, ...billingDetailsRest } = billing_details;
        createStripePaymentMethod(
          {
            card,
            stripe,
            billing_details: { name, email },
          },
          {
            onSuccess(stripe_payment_method) {
              // 2. update customer PM
              createPaymentMethod(
                {
                  ...(billingDetailsRest.address || {}),
                  stripe_payment_method,
                },
                {
                  onError(error, updateVariables, onMutateResult, context) {
                    if (error?.status === 500) {
                      error = {
                        message: "Invalid credit card details",
                        status: 500,
                      };
                    }

                    setError(error);
                    setIsLoading(false);

                    options?.onError?.(
                      error,
                      variables,
                      onMutateResult,
                      context,
                    );
                  },
                  onSuccess(data, updateVariables, onMutateResult, context) {
                    setError(null);
                    setIsLoading(false);

                    options?.onSuccess?.(
                      data,
                      variables,
                      onMutateResult,
                      context,
                    );

                    return queryClient.invalidateQueries({
                      queryKey: ["/account/payment_methods"],
                    });
                  },
                },
              );
            },
            onError(error) {
              setError(error);
              setIsLoading(false);
            },
          },
        );
      },
      [createStripePaymentMethod, queryClient, createPaymentMethod],
    );

    return {
      isPending:
        isLoading ||
        loadingCreateStripePaymentMethod ||
        loadingCreatePaymentMethod,
      error:
        error || createStripePaymentMethodError || createPaymentMethodError,
      mutate: updatePaymentMethod,
    };
  },

  useDefaultPaymentMethod({ enabled } = {}) {
    const { isLoading, error, data, refetch } = useQuery<
      unknown,
      unknown,
      IStripePaymentMethod[]
    >("/account/payment_methods", { enabled });

    return {
      isLoading,
      error,
      data: data?.find(
        (paymentMethod: IStripePaymentMethod) => paymentMethod.default,
      ),
      refetch,
    };
  },
};
